import React, { useEffect, useCallback } from "react";
import { Button, Divider, Grid, Link, makeStyles, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useContentFooter, useContentHeader } from "hooks/mainContent";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { quoteSteps } from "features/quote/constants";
import FooterInfo from "./FooterInfo";
import SectionTitle from "features/common/components/SectionTitle";
import QuoteStepper from "features/quote/components/QuoteStepper";
import QuoteFooter from "features/quote/components/QuoteFooter";
import { emptyArray, emptyString } from "constants/defaultValues";
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { addToast } from "components/notifications";
import { QUOTE_WITH_PRODUCTS_QUERY } from "features/quote/queries";
import Product from "./Product";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { REMOVE_PRODUCT, SEND_PRODUCTS_ON_EMAIL, SET_DATA_COMPLETION } from "../mutations";
import { convertBase64ToFile } from "utils/fileConverter";
import fileSaver from "file-saver";
import { DOWNLOAD_PRODUCTS_QUERY } from "../queries";
import ProductHistoriesContainer from "./ProductHistories/ProductHistoriesContainer";
import productPageStyle from "features/products/styles";
import { AssignmentTurnedIn, WebAsset } from "@material-ui/icons";
import { useApolloClient, useMutation } from "@apollo/client";
import { invalidateApolloClientCache } from "utils/functions";
import { quoteStatus } from "constants/quoteStatus";
import AddCircle from "@material-ui/icons/AddCircle";
import { COMPUTE_PRODUCTS, EXECUTE_ZOOT_RISK_CALL } from "features/quote/mutations";
import Fragments from "features/quote/fragments";
import { GET_PRODUCT_TYPES } from "features/products/queries";
import { get, set, useStateLens } from "@totalsoft/react-state-lens";
import useUserData from "hooks/useUserData";

const useStyles = makeStyles(productPageStyle);

function ProductsContainer() {
  const { t } = useTranslation();
  const history = useHistory();
  const clientQuery = useClientQueryWithErrorHandling();
  const client = useApolloClient();
  const location = useLocation();
  const params = useParams();
  const quoteId = Number(params.quoteId);
  const [, setFooter] = useContentFooter();
  const [, setHeader] = useContentHeader();
  const productHistoriesContainerProps = useStateLens({ open: false });
  const classes = useStyles();
  const userData = useUserData();

  useEffect(() => {
    if (location.state?.errorMessage) addToast(location.state.errorMessage, "warning");
  }, [t, location]);

  const [computeProducts] = useMutationWithErrorHandling(COMPUTE_PRODUCTS);
  const tryComputeProducts = useCallback(async () => {
    const { data } = await computeProducts({ variables: { quoteId } });
    if (data?.computeProducts?.length === 0) {
      const errorMessage = t("DialogMessages.SystemWasNotAbleToProgressYourQuote");
      history.push({ pathname: `/quotes/${quoteId}/assets`, state: { errorMessage } });
    } else {
      data?.computeProducts.forEach((computedProduct) => {
        client.cache.modify({
          id: client.cache.identify({ __typename: "Quote", quoteId }),
          fields: {
            quoteProducts(quoteProducts) {
              const quoteProduct = client.cache.writeFragment({
                data: computedProduct,
                fragment: Fragments.quoteProduct,
              });
              return [...quoteProducts, quoteProduct];
            },
          },
        });
      });
    }
    invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, ["quoteProductHistories"]);
  }, [quoteId, computeProducts, history, t, client]);

  const { data, loading } = useQueryWithErrorHandling(QUOTE_WITH_PRODUCTS_QUERY, {
    variables: { quoteId },
    onCompleted: (data) => {
      if (data?.quote?.quoteProducts?.length < 1) {
        tryComputeProducts();
      }
    },
  });

  const quote = data?.quote;
  const quoteProducts = data?.quote?.quoteProducts || emptyArray;

  const { data: productTypesData } = useQueryWithErrorHandling(GET_PRODUCT_TYPES, {
    skip: quoteProducts.length < 1,
    variables: { quoteId },
  });
  const productTypes = productTypesData?.productTypesByQuoteId || emptyArray;

  const [setDataCompletion] = useMutationWithErrorHandling(SET_DATA_COMPLETION, {
    variables: { quoteId },
  });

  const [executeZootRiskCall] = useMutation(EXECUTE_ZOOT_RISK_CALL);

  const handleComplete = useCallback(async () => {
    if (quote.isEstimation) {
      history.push({ pathname: `/quotes/${quoteId}/client` });
      return;
    }
    if (quote.quoteStatusId === quoteStatus.noFinanceProduct.id) {
      await setDataCompletion();
      executeZootRiskCall({ variables: { quoteId: quote.quoteId, currentStep: -3 } });
      history.push({ pathname: `/quotes/${quoteId}/datacompletion` });
    } else {
      history.push({ pathname: `/quotes/${quoteId}/datacompletion` });
    }
  }, [history, executeZootRiskCall, quote, quoteId, setDataCompletion]);

  const handleBack = useCallback(() => history.push({ pathname: `/quotes/${quoteId}/assets` }), [history, quoteId]);

  useEffect(() => {
    setHeader(
      <QuoteStepper
        quoteId={quoteId}
        activeStepId={quoteSteps.financeProduct}
        nextStepId={quoteSteps.dataCompletion}
        disabledNextStep={!quoteProducts.some((p) => p.selected)}
        onHandleNextStep={handleComplete}
      />
    );
  }, [t, setHeader, quoteId, quoteProducts, handleComplete]);

  useEffect(() => {
    if (!quote) return;
    setFooter(
      <QuoteFooter
        btnBackLabel={t("General.Asset")}
        btnNextLabel={quote?.isEstimation ? t("General.NewQuote") : t("General.CompleteData")}
        btnBackIcon={<WebAsset />}
        btnNextIcon={quote?.isEstimation ? <AddCircle /> : <AssignmentTurnedIn />}
        handleBack={handleBack}
        handleComplete={handleComplete}
        btnBackDisabled={false}
        btnNextDisabled={!quoteProducts.some((p) => p.selected)}
        btnNextExists={!userData?.userRights?.hasEstimatorOnlyRight}
        quoteId={quoteId}
      />
    );
  }, [
    t,
    setFooter,
    quoteId,
    quoteProducts,
    handleBack,
    handleComplete,
    quote,
    userData.userRights?.hasEstimatorOnlyRight,
  ]);

  const handleViewHistory = useCallback(
    (productTypeId) => () => {
      (productHistoriesContainerProps |> set)({ open: true, productTypeId });
    },
    [productHistoriesContainerProps]
  );

  const [removeProduct] = useMutationWithErrorHandling(REMOVE_PRODUCT);

  const handleRemoveProduct = useCallback(
    (id) => {
      removeProduct({ variables: { quoteId, quoteProductId: id } });
      invalidateApolloClientCache(client, {
        __typename: "QuoteProduct",
        quoteProductId: id,
      });
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, ["quoteProductHistories"]);
    },
    [quoteId, client, removeProduct]
  );

  const handleDownloadProducts = useCallback(
    async (products) => {
      const { data } = await clientQuery(DOWNLOAD_PRODUCTS_QUERY, {
        variables: { quoteId: quote.quoteId, products },
        fetchPolicy: "no-cache",
      });

      if (!data) return;

      const fileURL = convertBase64ToFile(data.downloadProducts.content);
      fileSaver.saveAs(fileURL, data.downloadProducts.name);
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, ["quoteProductHistories"]);
    },
    [clientQuery, quote, client, quoteId]
  );

  const [sendProductsOnEmail] = useMutationWithErrorHandling(SEND_PRODUCTS_ON_EMAIL, {
    onCompleted: () => {
      addToast(t("ToastNotifications.EmailMessageWasSent"), "success");
    },
  });

  const handleSendProductsOnEmail = useCallback(
    (email, products) => {
      sendProductsOnEmail({ variables: { quoteId: quote.quoteId, email, products } });
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, ["quoteProductHistories"]);
    },
    [sendProductsOnEmail, quote, client, quoteId]
  );

  if ((loading && !quote) || quoteProducts.length === 0) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <>
      <SectionTitle title={t("Product.SelectFinanceProducts")} subtitle={t("Product.SelectDesiredFinanceProduct")} />
      {[...productTypes]
        ?.sort((a, b) => a.id - b.id)
        ?.map((productType) => {
          const quoteProductsByProductType = quoteProducts.filter((p) => p.productTypeId === productType.id);
          return (
            <Grid container key={productType.name} className={classes.width100}>
              <Grid item xs={12}>
                <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                  <Typography>{productType.name}</Typography>
                  <Button variant="text" size="small" className={classes.viewHistoryButton}>
                    <Link
                      id="product_viewHistory"
                      underline="always"
                      color="primary"
                      onClick={handleViewHistory(productType.id)}
                    >
                      {t("Product.ViewHistory")}
                    </Link>
                  </Button>
                </Grid>
                <Divider />
              </Grid>
              {quoteProductsByProductType.map((product, index) => (
                <Grid item xs={12} key={index}>
                  <Product
                    key={product.quoteProductId}
                    product={product}
                    onRemoveProduct={handleRemoveProduct}
                    onDownloadProducts={handleDownloadProducts}
                    onSendProductsOnEmail={handleSendProductsOnEmail}
                    currencyCode={quote.currencyCode}
                    showAmountsWithVAT={quote.showAmountsWithVAT}
                    boolFinanceProductsDisabled={quote.quoteStatusId <= quoteStatus.pendingDecision.id}
                  />
                </Grid>
              ))}
            </Grid>
          );
        })}
      {(productHistoriesContainerProps.open |> get) && (
        <ProductHistoriesContainer
          productTypes={productTypes}
          productHistoriesContainerProps={productHistoriesContainerProps}
          quoteId={quote.quoteId}
          showAmountsWithVAT={quote.showAmountsWithVAT || false}
          currencyCode={quote.currencyCode || emptyString}
        />
      )}
      {!userData?.useDownloadProductInStep4 && (
        <FooterInfo
          quoteProducts={quoteProducts}
          currencyCode={quote.currencyCode || emptyString}
          showAmountsWithVAT={quote.showAmountsWithVAT || false}
          boolFinanceProductsDisabled={quote.quoteStatusId <= quoteStatus.pendingDecision.id}
          onDownloadProducts={handleDownloadProducts}
          onSendProductsOnEmail={handleSendProductsOnEmail}
        />
      )}
    </>
  );
}

export default ProductsContainer;
